/**
 * AV telemetry envelope — the cross-track schema shared with the ingest API.
 *
 * The spine is vendor-INVARIANT: it carries only what ingest needs to route and attribute a
 * record without opening the payload. Vendor-named identity (Chime's meetingId, attendeeId,
 * mediaRegion) rides inside `payload` alongside the metrics, verbatim — no renaming, no
 * regrouping, no client-side downsampling. Mapping vendor vocabulary onto ClearSign's is the
 * server-side per-vendor translator's job, which is what keeps new diagnoses retroactively
 * computable over telemetry already sitting in S3.
 *
 * Data minimization: no business identifiers appear anywhere in this envelope — no
 * packageGuid, packageUserGuid, activeUser, or device-group lists. Identity is attached
 * server-side at ingest. `deviceCode` is absent for a different reason: it identifies the
 * browser and belongs in the S3 key, not repeated on every record.
 */

/** Bump when the SPINE changes shape. Vendor payload changes are the translator's problem. */
export const AV_TELEMETRY_SCHEMA_VERSION = 1;

export type AvTelemetryRecordType = 'meta' | 'tick' | 'event';

/** Lowercase on the wire — the translator dispatches on this exact string. */
export type AvTelemetryVendor = 'chime' | 'vonage';

/**
 * How a vendor supplies its periodic metrics.
 *
 * - `push`  — the SDK invokes a callback on its own cadence (Chime: ~1 Hz `metricsDidReceive`).
 * - `poll`  — no callback exists; the sampler must drive a timer and ask (Vonage: `getStats()`).
 *
 * This distinction is why cadence lives in the shared sampler rather than in each source.
 */
export type AvTelemetryCadenceMode = 'push' | 'poll';

/** Target sampling period for `poll` vendors, matching Chime's native push rate. */
export const AV_TELEMETRY_POLL_INTERVAL_MS = 1_000;

/**
 * The vendor-invariant spine. Every record written to S3 has exactly these fields.
 *
 * `seq` is a monotonic per-session record ordinal assigned at capture, so ordering survives
 * out-of-order uploads and partial chunk loss. Chunk-level sequencing is the uploader's
 * concern and appears in the S3 key, not here.
 */
export interface AvTelemetryEnvelope<TPayload = unknown> {
  type: AvTelemetryRecordType;
  schemaVersion: number;
  /**
   * Client clock, **Unix epoch milliseconds** (`Date.now()`), matching the `{unixMs}` in the
   * S3 key. Epoch ms is inherently UTC, so there is no zone to carry.
   *
   * Client clocks drift and can jump backward on NTP correction; the server stamps its own
   * receive time as S3 object metadata at ingest, and cross-device evaluation should trust
   * that one. `seq` — not this field — is what guarantees intra-session ordering.
   */
  occurredAt: number;
  vendor: AvTelemetryVendor;
  /**
   * Vendor SDK version, on every record rather than only on `meta`.
   *
   * This is parsing metadata, not description. Vendor identity now lives inside `payload`,
   * so attributing a record means reading the payload — and reading the payload correctly
   * wants the SDK version. Sourcing that version from `meta` would make it circular: you
   * need the payload's session id to find `meta`, and `meta` to parse the payload. Keeping
   * it on the spine makes attribution single-pass and survives a lost `meta` record, which
   * is exactly the orphaned-chunk case. Repeated identical strings cost ~nothing gzipped.
   */
  vendorVersion: string;
  seq: number;
  payload: TPayload;
}

/**
 * Low-entropy User-Agent Client Hints.
 *
 * Deliberately the low-entropy trio only — available synchronously, with no permission
 * prompt and no promise to await. The app collects HIGH-entropy hints elsewhere
 * (`DeviceGroupSelectors.selectClientHintsForDevice`) for device validation, gated behind
 * its own `ClientHints` feature flag; that set is both more data than telemetry needs and
 * conditionally absent, so it is not reused here.
 *
 * All fields are nullable because `navigator.userAgentData` is Chromium-only.
 */
export interface AvTelemetryClientHints {
  brands: { brand: string; version: string }[] | null;
  mobile: boolean | null;
  platform: string | null;
}

/**
 * Session-constant browser context, emitted once as part of the session's `meta` payload.
 *
 * Browser facts are not vendor-specific, so this stays shared and each vendor's meta payload
 * composes it with its own identity fields. Kept off every tick so the 1 Hz firehose stays
 * lean. Raw values only — `userAgent` is shipped verbatim rather than parsed into
 * browser/OS, because parsing is the translator's job.
 *
 * Deliberately no `platform` field: `navigator.platform` is deprecated, and the value is
 * redundant here — `clientHints.platform` covers Chromium, and elsewhere the platform token
 * is recoverable from the verbatim `userAgent`, which the backend already parses.
 */
export interface AvTelemetryClientContext {
  userAgent: string;
  clientHints: AvTelemetryClientHints;
  language: string;
  hardwareConcurrency: number | null;
  deviceMemoryGb: number | null;
  screenWidth: number | null;
  screenHeight: number | null;
  devicePixelRatio: number | null;
}
